import { Box, Button, IconButton, Stack, TextField, Typography } from "@mui/material";
import { useTBContext } from "../../Contexts/useTBStates";
import { HdcpItemDisplay } from "./HdcpItemDisplay";
import DeleteIcon from '@mui/icons-material/Delete';


interface hdcpDisplayProps {
    level: 'event' | 'round';
    keyName: string;
    locked?: boolean;
    orient?: 'row' | 'column';
}

/** Primary hdcp usage is the first array element; supports legacy object shape. */
const getPrimaryHdcpUsage = (hdcp: unknown) => Array.isArray(hdcp) ? hdcp[0] : (hdcp as object | undefined);

export const HandicapDisplay = ({ level, keyName, locked, orient = 'column' }: hdcpDisplayProps) => {
    const { viewEvent, roundValue, setViewEvent, factor, editMode } = useTBContext();

    const hdcpUsageArr = level === 'event' ? viewEvent.hdcpUsage : viewEvent.rounds[roundValue-1]?.hdcpUsage;
    const hdcpUsage = getPrimaryHdcpUsage(hdcpUsageArr);
    const hdcpUsageExists = hdcpUsage != null && Object.keys(hdcpUsage).length > 0;

    return (
        <>{hdcpUsageExists && keyName === 'type' && 
                <HdcpItemDisplay
                    level={level}
                    key={`${level}-hdcp-type`}
                    orient={orient}
                    label={'Type'}
                    keyName={keyName}
                    labelField={'type'}
                    optionsKeyName={'HdcpTypes'}    
                    removable={false}
                    defaultValue={null}
                    locked={locked}
                />
        }
        {hdcpUsageExists && keyName === 'indReduction' && hdcpUsage?.type === 'Adjusted' &&
                <HdcpItemDisplay
                    level={level}
                    key={`${level}-hdcp-indReduction`}
                    orient={orient}
                    label={'Flat Reduction %'}
                    keyName={keyName}  
                    removable={false}
                    defaultValue={null}
                    locked={locked}
                />
        }
        {hdcpUsageExists && keyName === 'compositeType' && hdcpUsage?.type === 'Composite' &&
            <Stack direction='column' spacing={1}>
                <HdcpItemDisplay
                    level={level}
                    key={`${level}-hdcp-compositeType`}
                    orient={orient}
                    label={'Method'}
                    keyName={'type'}
                    labelField={'type'}
                    optionsKeyName={'HdcpCompositeTypes'}    
                    removable={false}
                    defaultValue={null}
                    composite={true}
                    locked={locked}
                />
                <HdcpItemDisplay
                    level={level}
                    key={`${level}-hdcp-compositeReduction`}
                    orient={orient}
                    label={'Comp Reduction %'}
                    keyName={'reduction'}   
                    removable={false}
                    defaultValue={null}
                    composite={true}
                    locked={locked}
                />
            </Stack>
        }
        {hdcpUsageExists && keyName === 'compositeBuild' && hdcpUsage?.type === 'Composite' &&
            <Stack key={`${level}-hdcp-compositeBuild`} direction='column' spacing={1}>
                <Stack direction='row' spacing={0} alignItems='center'>
                    <Typography sx={{ fontSize: 14*factor, fontWeight: 500, flex: 1, textAlign: 'center' }}>Hdcp Rank</Typography>
                    <Typography sx={{ fontSize: 14*factor, fontWeight: 500, flex: 1, textAlign: 'center' }}>Weight %</Typography>
                    <Box sx={{ padding: 0, width: 20*factor, height: 20*factor }} />
                </Stack>
                {(hdcpUsage?.hdcpComposite?.build ?? []).map((buildItem: any, index: number) => (
                    <Stack direction='row' spacing={1} alignItems='center'>
                        <Typography sx={{ fontSize: 14*factor, flex: 1, textAlign: 'center' }}>{buildItem.hdcpPosition}</Typography>
                        <TextField
                            key={`${level}-hdcp-compositeBuild-${index}`}
                            disabled={!editMode || locked}
                            value={buildItem.hdcpWeight}
                            type={'number'}
                            onChange={(e) => {
                                const newValue = e.target.value;
                                const formattedValue = Number(newValue);

                                if (level === 'event') {
                                    const prim = getPrimaryHdcpUsage(viewEvent.hdcpUsage);
                                    const primComp = prim?.hdcpComposite ?? {};
                                    const updatedPrim = { ...prim, hdcpComposite: { ...primComp, build: (primComp.build ?? []).map((b: any, i: number) => i === index ? { ...b, hdcpWeight: formattedValue } : b) } };
                                    const updatedRounds = viewEvent.rounds.map((round: any) => {
                                        const rPrim = getPrimaryHdcpUsage(round.hdcpUsage);
                                        const rComp = rPrim?.hdcpComposite ?? {};
                                        return { ...round, hdcpUsage: [{ ...rPrim, hdcpComposite: { ...rComp, build: (rComp.build ?? []).map((b: any, i: number) => i === index ? { ...b, hdcpWeight: formattedValue } : b) } }] };
                                    });
                                    setViewEvent((prevState: any) => ({ ...prevState, hdcpUsage: [updatedPrim], rounds: updatedRounds }));
                                }
                                else {
                                    const updatedRounds = viewEvent.rounds.map((round: any) => {
                                        if (round.roundNumber === roundValue) {
                                            const rPrim = getPrimaryHdcpUsage(round.hdcpUsage);
                                            const rComp = rPrim?.hdcpComposite ?? {};
                                            return { ...round, hdcpUsage: [{ ...rPrim, hdcpComposite: { ...rComp, build: (rComp.build ?? []).map((b: any, i: number) => i === index ? { ...b, hdcpWeight: formattedValue } : b) } }] };
                                        }
                                        return round;
                                    });
                                    setViewEvent((prevState: any) => ({ ...prevState, rounds: updatedRounds }));
                                }
                            }}
                            sx={{
                                padding: 0,
                                "& .MuiInputBase-input": { fontSize: 14*factor, height: 14*factor, padding: 1 },
                                '& .MuiOutlinedInput-root': {padding: '0px 0px'},
                                '& input': {textAlign: 'left', lineHeight: 'normal'},
                                flex: 1,
                                width: '100%',
                            }}
                            InputProps={{
                                sx: {
                                    fontSize: 14*factor,
                                },
                            }}
                        />
                        {(index > 1 && editMode && !locked) ?
                            <IconButton 
                                sx={{ padding: 0, width: 20*factor, height: 20*factor }}
                                disabled={!editMode}
                                onClick={() => {
                                setViewEvent((prevState: any) => {
                                    const p = getPrimaryHdcpUsage(prevState.hdcpUsage);
                                    const c = p?.hdcpComposite ?? {};
                                    return { ...prevState, hdcpUsage: [{ ...p, hdcpComposite: { ...c, build: (c.build ?? []).filter((_: any, i: number) => i !== index) } }] };
                                })
                            }}>
                                <DeleteIcon sx={{ fontSize: 20*factor, color: 'error.main' }} />
                            </IconButton>
                            :
                            <Box sx={{ padding: 0, width: 20*factor, height: 20*factor }} />
                        }
                    </Stack>
                ))}
                <Stack direction='row' spacing={1} alignItems='center'>
                    <Typography sx={{ fontSize: 14*factor, fontWeight: 500, flex: 1, textAlign: 'center' }}>Total</Typography>
                    <Typography sx={{ fontSize: 14*factor, fontWeight: 500, flex: 1, textAlign: 'center' }}>
                        {(hdcpUsage?.hdcpComposite?.build ?? []).reduce((acc: number, buildItem: any) => acc + buildItem.hdcpWeight, 0)}
                    </Typography>
                    <Box sx={{ padding: 0, width: 20*factor, height: 20*factor }} />
                </Stack>
                {hdcpUsage?.hdcpComposite?.type === 'Custom' && editMode && !locked && <Button variant='contained' color='primary' onClick={() => {

                    if (level === 'event') {
                        const build = hdcpUsage?.hdcpComposite?.build ?? [];
                        const newEntry = { hdcpPosition: build.length + 1, hdcpWeight: 0 };
                        const updatedPrim = { ...hdcpUsage, hdcpComposite: { ...hdcpUsage?.hdcpComposite, build: [...build, newEntry] } };
                        const updatedRounds = viewEvent.rounds.map((round: any) => {
                            const rPrim = getPrimaryHdcpUsage(round.hdcpUsage);
                            const rBuild = rPrim?.hdcpComposite?.build ?? [];
                            return { ...round, hdcpUsage: [{ ...rPrim, hdcpComposite: { ...rPrim?.hdcpComposite, build: [...rBuild, { hdcpPosition: rBuild.length + 1, hdcpWeight: 0 }] } }] };
                        });
                        setViewEvent((prevState: any) => ({ ...prevState, hdcpUsage: [updatedPrim], rounds: updatedRounds }));
                    }
                    else {
                        const updatedRounds = viewEvent.rounds.map((round: any) => {
                            if (round.roundNumber === roundValue) {
                                const rPrim = getPrimaryHdcpUsage(round.hdcpUsage);
                                const rBuild = rPrim?.hdcpComposite?.build ?? [];
                                return { ...round, hdcpUsage: [{ ...rPrim, hdcpComposite: { ...rPrim?.hdcpComposite, build: [...rBuild, { hdcpPosition: rBuild.length + 1, hdcpWeight: 0 }] } }] };
                            }
                            return round;
                        });
                        setViewEvent((prevState: any) => ({ ...prevState, rounds: updatedRounds }));
                    }
                }}>Add Player</Button>}
            </Stack>
        }
        </>
    )
}
